/**
 * Figursky theme runtime.
 *
 * Replaces the Nuxt layout store (app/stores/layout.ts) and checkout store:
 * drawers, the header sub-nav curtains, the mobile nav overlay and the Ajax
 * cart. No framework, no build step.
 *
 * Public API:
 *   window.figursky.openDrawer(id)
 *   window.figursky.closeDrawer(id)
 *   window.figursky.cart.add(variantId, quantity, properties)
 *   window.figursky.cart.update(lineKey, quantity)
 *   window.figursky.cart.note(text)
 *   window.figursky.lockScroll()   — used by assets/popups.js (Task 14)
 *   window.figursky.unlockScroll()
 *   window.figursky.focus.trap(element)    — used by assets/popups.js
 *   window.figursky.focus.release(element)
 */
(function () {
  'use strict';

  var CART_SECTION_ID = 'cart-drawer';
  var NOTE_SELECTOR = '#cart-note, #cart-page-note, [name="note"]';
  // True from the first keystroke in the note field until that text has been
  // saved to the cart; renderCartDrawer only carries the typed note across a
  // re-render while this is set, so a note edited in another tab is not
  // silently overwritten by this tab's stale copy.
  var noteDirty = false;
  var NOTE_DEBOUNCE = 600;
  var BODY_LOCKED_CLASS = 'is-locked';
  var NAV_OVERLAY_OPEN_CLASS = 'is-nav-overlay-open';
  var SCROLLED_CLASS = 'is-scrolled';

  var root = document.documentElement;

  /* ------------------------------------------------------- manual locking */
  // A reference count rather than a boolean so more than one caller (e.g. the
  // promo popup plus a future drawer) can hold the lock independently without
  // one closing it out from under the other.
  var manualLockCount = 0;

  function routeRoot() {
    var configured = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return configured || '/';
  }

  function cartUrl(file) {
    return routeRoot() + 'cart/' + file;
  }

  /* ----------------------------------------------------------------- body */

  function syncBodyLock() {
    var hasOpenDrawer = !!document.querySelector('[data-drawer].is-open');
    var navOverlayOpen = root.classList.contains(NAV_OVERLAY_OPEN_CLASS);
    var subNavOpen = !!document.querySelector('[data-header].sub-opened');

    root.classList.toggle(BODY_LOCKED_CLASS, hasOpenDrawer || navOverlayOpen || subNavOpen || manualLockCount > 0);
  }

  function lockScroll() {
    manualLockCount += 1;
    syncBodyLock();
  }

  function unlockScroll() {
    manualLockCount = Math.max(0, manualLockCount - 1);
    syncBodyLock();
  }

  /* ---------------------------------------------------------------- focus */
  // A single shared focus trap for every modal surface in the theme: the
  // `.half-screen` drawers here and the two popups in assets/popups.js, which
  // reach it through window.figursky.focus. Each trap remembers the element
  // that had focus when it opened and hands focus back on release, so a
  // keyboard shopper returns to the control they came from.

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  // Innermost trap last; a stack rather than a single reference so a drawer
  // opened from inside a popup (or vice versa) unwinds in the right order.
  var focusTraps = [];

  function isVisible(element) {
    if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  function focusableIn(container) {
    var nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      if (isVisible(nodes[i])) out.push(nodes[i]);
    }
    return out;
  }

  function focusTrapIndex(element) {
    for (var i = 0; i < focusTraps.length; i++) {
      if (focusTraps[i].element === element) return i;
    }
    return -1;
  }

  function safeFocus(element) {
    if (!element || !element.focus) return;
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  }

  // The close button first: it is the least destructive place to land, and it
  // is what a shopper reaching for Escape's mouse equivalent expects.
  function initialFocus(element) {
    var items = focusableIn(element);
    for (var i = 0; i < items.length; i++) {
      if (items[i].matches('[data-drawer-close], [data-popup-close]')) return items[i];
    }
    if (items.length) return items[0];

    if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
    return element;
  }

  function trapFocus(element) {
    if (!element || focusTrapIndex(element) !== -1) return;
    focusTraps.push({ element: element, returnTo: document.activeElement });
    safeFocus(initialFocus(element));
  }

  function releaseFocus(element, keepFocus) {
    var index = focusTrapIndex(element);
    if (index === -1) return;

    var entry = focusTraps[index];
    focusTraps.splice(index, 1);

    if (keepFocus) return;

    var returnTo = entry.returnTo;
    if (returnTo && returnTo.focus && document.contains(returnTo) && isVisible(returnTo)) {
      safeFocus(returnTo);
    }
  }

  // Re-point an existing trap at freshly rendered markup (the cart drawer is
  // replaced wholesale after every Ajax mutation) without bouncing focus back
  // to whatever opened it.
  function retargetFocusTrap(oldElement, newElement) {
    var index = focusTrapIndex(oldElement);
    if (index === -1 || !newElement) return;
    focusTraps[index].element = newElement;
    if (!newElement.contains(document.activeElement)) {
      safeFocus(initialFocus(newElement));
    }
  }

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Tab' || !focusTraps.length) return;

      var entry = focusTraps[focusTraps.length - 1];
      var items = focusableIn(entry.element);
      if (!items.length) {
        event.preventDefault();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (!entry.element.contains(active)) {
        event.preventDefault();
        safeFocus(event.shiftKey ? last : first);
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        safeFocus(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        safeFocus(first);
      }
    },
    true
  );

  /* -------------------------------------------------------------- drawers */

  // HalfScreen.vue fired `before-enter` once per open, which is what drove
  // Cart.vue's displayContent(). The CSS equivalent is the `is-entering` class
  // below: added only when a closed drawer opens, so the Section Rendering
  // re-render after every cart mutation cannot replay the stagger. It is
  // removed once the longest animation has finished — the animated elements
  // then sit at their normal, already-visible styles, so the removal is
  // invisible (and under `prefers-reduced-motion: reduce` nothing ran at all).
  var ENTER_CONTENT_DELAY = 440;
  var ENTER_DURATION = 640;
  var ENTER_CART_STAGGER = 300;
  var ENTER_CART_FOOTER_DELAY = 640;

  var enterTimers = {};

  function enterDuration(drawer) {
    var longest = ENTER_CONTENT_DELAY + ENTER_DURATION;

    if (drawer.classList.contains('cart')) {
      var lines = drawer.querySelectorAll('[data-cart-line]').length;
      if (lines > 0) {
        longest = Math.max(longest, ENTER_CONTENT_DELAY + ENTER_CART_STAGGER * (lines - 1) + ENTER_DURATION);
      }
      longest = Math.max(longest, ENTER_CART_FOOTER_DELAY + ENTER_DURATION);
    }

    return longest;
  }

  function startEnterAnimation(id, drawer) {
    drawer.classList.add('is-entering');

    if (enterTimers[id]) window.clearTimeout(enterTimers[id]);
    enterTimers[id] = window.setTimeout(function () {
      enterTimers[id] = null;
      drawer.classList.remove('is-entering');
    }, enterDuration(drawer));
  }

  function stopEnterAnimation(id, drawer) {
    if (enterTimers[id]) {
      window.clearTimeout(enterTimers[id]);
      enterTimers[id] = null;
    }
    drawer.classList.remove('is-entering');
  }

  function getDrawer(id) {
    return document.querySelector('[data-drawer="' + id + '"]');
  }

  function openDrawer(id) {
    var drawer = getDrawer(id);
    if (!drawer) return;
    closeSubNav();
    closeNavOverlay();

    var wasOpen = drawer.classList.contains('is-open');
    drawer.classList.add('is-open');
    syncBodyLock();
    // Re-opening an already-open drawer (every cart mutation ends in
    // openDrawer('cart')) must not stack a second trap or steal focus back
    // from whatever the shopper is using inside it.
    if (!wasOpen) {
      trapFocus(drawer);
      startEnterAnimation(id, drawer);
    }
  }

  function closeDrawer(id) {
    var drawer = getDrawer(id);
    if (!drawer) return;
    drawer.classList.remove('is-open');
    stopEnterAnimation(id, drawer);
    releaseFocus(drawer);
    syncBodyLock();
  }

  function closeAllDrawers() {
    var drawers = document.querySelectorAll('[data-drawer].is-open');
    for (var i = 0; i < drawers.length; i++) {
      drawers[i].classList.remove('is-open');
      stopEnterAnimation(drawers[i].getAttribute('data-drawer'), drawers[i]);
      releaseFocus(drawers[i]);
    }
    syncBodyLock();
  }

  /* ------------------------------------------------------- header sub-nav */

  function closeSubNav() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    header.classList.remove('sub-opened', 'is-sub-left', 'is-sub-right');

    var actives = header.querySelectorAll('.app-sub-nav.is-active, .header__curtains-fig.is-active');
    for (var i = 0; i < actives.length; i++) {
      actives[i].classList.remove('is-active');
    }

    var toggles = header.querySelectorAll('[data-subnav-toggle]');
    for (var j = 0; j < toggles.length; j++) {
      toggles[j].classList.remove('is-current');
      toggles[j].setAttribute('aria-expanded', 'false');
    }

    syncBodyLock();
  }

  function toggleSubNav(toggle) {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var id = toggle.getAttribute('data-subnav-toggle');
    var type = toggle.getAttribute('data-subnav-type') || 'left';
    var panel = header.querySelector('[data-subnav="' + id + '"]');
    var wasOpen = panel && panel.classList.contains('is-active');

    closeSubNav();
    if (wasOpen || !panel) return;

    header.classList.add('sub-opened', 'is-sub-' + type);
    panel.classList.add('is-active');
    var figKey = toggle.getAttribute('data-subnav-fig-key');
    if (figKey) {
      var fig = header.querySelector('[data-subnav-fig="' + figKey + '"]');
      if (fig) fig.classList.add('is-active');
    }
    toggle.classList.add('is-current');
    toggle.setAttribute('aria-expanded', 'true');
    syncBodyLock();
  }

  /* --------------------------------------------------------- nav overlay */

  function closeNavOverlay() {
    var overlay = document.querySelector('[data-nav-overlay]');
    if (overlay) overlay.classList.remove('is-open');
    root.classList.remove(NAV_OVERLAY_OPEN_CLASS);
    collapseAllAccordions();

    var toggles = document.querySelectorAll('[data-nav-overlay-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-expanded', 'false');
    }

    syncBodyLock();
  }

  function toggleNavOverlay() {
    var overlay = document.querySelector('[data-nav-overlay]');
    if (!overlay) return;

    if (overlay.classList.contains('is-open')) {
      closeNavOverlay();
      return;
    }

    closeAllDrawers();
    closeSubNav();
    overlay.classList.add('is-open');
    root.classList.add(NAV_OVERLAY_OPEN_CLASS);

    var toggles = document.querySelectorAll('[data-nav-overlay-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-expanded', 'true');
    }

    syncBodyLock();
  }

  function collapseAllAccordions() {
    var panels = document.querySelectorAll('[data-nav-collapse].is-expanded');
    for (var i = 0; i < panels.length; i++) {
      panels[i].classList.remove('is-expanded');
    }

    var buttons = document.querySelectorAll('[data-nav-accordion].is-expanded');
    for (var j = 0; j < buttons.length; j++) {
      buttons[j].classList.remove('is-expanded');
      buttons[j].setAttribute('aria-expanded', 'false');
    }
  }

  function toggleAccordion(button) {
    var id = button.getAttribute('data-nav-accordion');
    var panel = document.querySelector('[data-nav-collapse="' + id + '"]');
    var wasExpanded = panel && panel.classList.contains('is-expanded');

    collapseAllAccordions();
    if (wasExpanded || !panel) return;

    panel.classList.add('is-expanded');
    button.classList.add('is-expanded');
    button.setAttribute('aria-expanded', 'true');
  }

  /* ------------------------------------------------------------ ajax cart */

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Cart request failed');
        }
        return data;
      });
    });
  }

  function syncCartCount(container) {
    var source = container.querySelector('[data-cart-count]');
    if (!source) return;

    var count = source.getAttribute('data-cart-count');
    var targets = document.querySelectorAll('[data-cart-count-target]');
    for (var i = 0; i < targets.length; i++) {
      targets[i].textContent = count;
    }
  }

  function renderCartDrawer() {
    var container = document.getElementById('shopify-section-' + CART_SECTION_ID);
    if (!container) return Promise.resolve();

    return fetch(routeRoot() + '?sections=' + CART_SECTION_ID)
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var html = sections[CART_SECTION_ID];
        if (!html) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.getElementById('shopify-section-' + CART_SECTION_ID) || parsed.body;
        var current = container.querySelector('[data-drawer]');
        var wasOpen = !!current && current.classList.contains('is-open');

        // The re-rendered markup re-seeds the textarea from `cart.note`, which
        // is whatever the server last stored — so anything typed since the
        // last save would be thrown away by the swap below. Carry it over, but
        // only while there IS unsaved typing: otherwise the server's note wins
        // (it may have been edited in another tab).
        var currentNote = container.querySelector(NOTE_SELECTOR);
        var pendingNote = currentNote && noteDirty ? currentNote.value : null;

        container.innerHTML = fresh.innerHTML;

        var next = container.querySelector('[data-drawer]');

        if (wasOpen && next) {
          next.classList.add('is-open');
          retargetFocusTrap(current, next);
        }

        var nextNote = container.querySelector(NOTE_SELECTOR);
        if (nextNote && pendingNote !== null && pendingNote !== nextNote.value) {
          nextNote.value = pendingNote;
        }

        syncCartCount(container);
      });
  }

  function mutateCart(url, body) {
    return postJson(url, body)
      .then(function (data) {
        // The /cart page renders the same lines as the drawer but is not
        // re-rendered by the Section Rendering API call below, so opening the
        // drawer over it would leave a stale list behind. Reload instead.
        if (document.querySelector('[data-cart-page]')) {
          window.location.reload();
          return data;
        }
        return renderCartDrawer().then(function () {
          openDrawer('cart');
          return data;
        });
      })
      .catch(function (error) {
        // eslint-disable-next-line no-console
        console.error('[figursky] cart', error);
        throw error;
      });
  }

  var cart = {
    add: function (variantId, quantity, properties) {
      var payload = { id: variantId, quantity: quantity || 1 };
      if (properties && Object.keys(properties).length > 0) {
        payload.properties = properties;
      }
      return mutateCart(cartUrl('add.js'), payload);
    },
    update: function (lineKey, quantity) {
      return mutateCart(cartUrl('change.js'), { id: lineKey, quantity: quantity });
    },
    note: function (text) {
      // Deliberately not mutateCart(): re-rendering the drawer would replace
      // the very textarea the shopper is typing in (losing the caret, and any
      // keystrokes since the debounce fired). The note changes nothing that is
      // rendered anywhere else, so there is nothing to re-render.
      return postJson(cartUrl('update.js'), { note: text }).catch(function (error) {
        // eslint-disable-next-line no-console
        console.error('[figursky] cart note', error);
        throw error;
      });
    },
  };

  /* ------------------------------------------------------------- bindings */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var drawerOpen = target.closest('[data-drawer-open]');
    if (drawerOpen) {
      event.preventDefault();
      openDrawer(drawerOpen.getAttribute('data-drawer-open'));
      return;
    }

    var drawerClose = target.closest('[data-drawer-close]');
    if (drawerClose) {
      event.preventDefault();
      closeDrawer(drawerClose.getAttribute('data-drawer-close'));
      return;
    }

    var quantityButton = target.closest('[data-cart-quantity]');
    if (quantityButton) {
      event.preventDefault();
      var line = quantityButton.closest('[data-cart-line]');
      if (line) line.classList.add('is-loading');
      cart
        .update(quantityButton.getAttribute('data-cart-quantity'), Number(quantityButton.getAttribute('data-quantity')))
        .catch(function () {
          if (line) line.classList.remove('is-loading');
        });
      return;
    }

    var subNavToggle = target.closest('[data-subnav-toggle]');
    if (subNavToggle) {
      event.preventDefault();
      toggleSubNav(subNavToggle);
      return;
    }

    if (target.closest('[data-subnav-close]')) {
      event.preventDefault();
      closeSubNav();
      return;
    }

    if (target.closest('[data-nav-overlay-toggle]')) {
      event.preventDefault();
      toggleNavOverlay();
      return;
    }

    var accordion = target.closest('[data-nav-accordion]');
    if (accordion) {
      event.preventDefault();
      toggleAccordion(accordion);
    }
  });

  /* ------------------------------------------------------------ cart note */
  // Delegated, because the drawer's markup is replaced after every Ajax cart
  // mutation. `change` covers blur / the native form submit path; the debounced
  // `input` saves while the shopper is still typing, so a note survives a
  // re-render (and a checkout click) even if they never leave the field.
  // The `<form method="post">` around it stays the no-JS fallback.

  var noteTimer = null;

  function noteFieldFrom(target) {
    if (!target || !target.closest || !target.matches) return null;
    if (!target.matches(NOTE_SELECTOR)) return null;
    return target.closest('[data-drawer="cart"], [data-cart-page]') ? target : null;
  }

  function saveNote(field) {
    if (noteTimer) {
      window.clearTimeout(noteTimer);
      noteTimer = null;
    }
    var value = field.value;
    cart
      .note(value)
      .then(function () {
        // Clean only if nothing was typed while the request was in flight —
        // the field may have been swapped by a re-render, so compare against
        // whatever note field is in the document now.
        var live = document.querySelector(NOTE_SELECTOR);
        if (!live || live.value === value) noteDirty = false;
      })
      .catch(function () {
        /* already logged; the form POST fallback still works */
      });
  }

  document.addEventListener('input', function (event) {
    var field = noteFieldFrom(event.target);
    if (!field) return;

    noteDirty = true;
    if (noteTimer) window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(function () {
      noteTimer = null;
      saveNote(field);
    }, NOTE_DEBOUNCE);
  });

  document.addEventListener('change', function (event) {
    var field = noteFieldFrom(event.target);
    if (field) saveNote(field);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    closeAllDrawers();
    closeSubNav();
    closeNavOverlay();
  });

  function syncScrolled() {
    root.classList.toggle(SCROLLED_CLASS, window.scrollY > 0);
  }

  window.addEventListener('scroll', syncScrolled, { passive: true });
  syncScrolled();

  window.figursky = {
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    cart: cart,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    focus: {
      trap: trapFocus,
      release: releaseFocus,
    },
  };
})();
